/**
 * Meters Page - Updated with full-page history view
 * Manage water meters and their readings
 */

import React, { useState, useEffect } from 'react';
import { MeterList } from '@components/meters/meter_list';
import { MeterReadingForm } from '@components/meters/meter_reading_form';
import { MeterHistory } from '@components/meters/meter_history';
import { PageLoading } from '@components/common/loading_spinner';
import { toast } from 'react-toastify';
import {
  Meter,
  CreateMeterReadingPayload,
  MeterReading,
  MeterReadingStats,
} from '@/types/meter_types';
import {
  fetch_meters,
  delete_meter,
  fetch_meter_readings,
  create_meter_reading,
  fetch_meter_reading_stats,
  create_meter,
  update_meter
} from '@services/meter_service';
import { fetch_clients } from '@services/client_service';
import { MeterForm, MeterFormData } from '@/components/meters/MeterForm';

// Client type for the dropdown
interface Client {
  id: string;
  name: string;
}

type PageView = 'list' | 'history';

export const MetersPage: React.FC = () => {
  // State management
  const [meters, set_meters] = useState<Meter[]>([]);
  const [clients, set_clients] = useState<Client[]>([]);
  const [is_loading, set_is_loading] = useState(true);
  const [is_reading_form_open, set_is_reading_form_open] = useState(false);
  const [is_submitting, set_is_submitting] = useState(false);
  const [current_view, set_current_view] = useState<PageView>('list');

  const [selected_meter, set_selected_meter] = useState<Meter | null>(null);
  const [meter_readings, set_meter_readings] = useState<MeterReading[]>([]);
  const [reading_stats, set_reading_stats] = useState<MeterReadingStats | undefined>();
  const [history_loading, set_history_loading] = useState(false);

  const [is_meter_form_open, set_is_meter_form_open] = useState(false);
  const [meter_for_edit, set_meter_for_edit] = useState<Meter | null>(null);

  // Load meters and clients on mount
  useEffect(() => {
    load_initial_data();
  }, []);

  const load_initial_data = async () => {
    try {
      set_is_loading(true);
      
      // Load meters and clients in parallel
      const [meters_data, clients_data] = await Promise.all([
        fetch_meters(),
        fetch_clients()
      ]);

      set_meters(Array.isArray(meters_data) ? meters_data : []);
      
      // Transform clients data to match the required format
      const transformed_clients = Array.isArray(clients_data) 
        ? clients_data.map(client => ({
            id: client.id.toString(),
            name: client.name
          }))
        : [];
      
      set_clients(transformed_clients);
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Failed to load data');
      set_meters([]);
      set_clients([]);
    } finally {
      set_is_loading(false);
    }
  };

  const load_meters = async () => {
    try {
      set_is_loading(true);
      const data = await fetch_meters();
      set_meters(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading meters:', error);
      toast.error('Failed to load meters');
      set_meters([]);
    } finally {
      set_is_loading(false);
    }
  };

const load_meter_history = async (meter: Meter) => {
  try {
    set_history_loading(true);
    
    console.log("=== LOADING METER HISTORY ===");
    console.log("Meter ID:", meter.id);

    const [readings, stats] = await Promise.all([
      fetch_meter_readings(meter.id),
      fetch_meter_reading_stats(meter.id)
    ]);

    console.log("Raw readings from API:", readings);
    console.log("Readings is array?", Array.isArray(readings));
    console.log("Readings length:", readings?.length);
    console.log("First reading:", readings?.[0]);
    
    const processed_readings = Array.isArray(readings) ? readings : [];
    console.log("Processed readings:", processed_readings);
    console.log("Processed readings length:", processed_readings.length);
    
    set_meter_readings(processed_readings);
    set_reading_stats(stats);
    
    console.log("State should be updated now");
    console.log("=== END LOADING ===");
  } catch (error) {
    console.error('Error loading meter history:', error);
    toast.error('Failed to load meter history');
    set_meter_readings([]);
    set_reading_stats(undefined);
  } finally {
    set_history_loading(false);
  }
};

  const handle_add_reading = (meter: Meter) => {
    set_selected_meter(meter);
    set_is_reading_form_open(true);
  };

  const handle_view_meter = async (meter: Meter) => {
    set_selected_meter(meter);
    set_current_view('history');
    await load_meter_history(meter);
  };

  const handle_back_to_list = () => {
    set_current_view('list');
    set_selected_meter(null);
  };

  const handle_add_meter = () => {
    set_meter_for_edit(null);
    set_is_meter_form_open(true);
  };

  const handle_edit_meter = (meter: Meter) => {
    set_meter_for_edit(meter);
    set_is_meter_form_open(true);
  };

  const handle_meter_submit = async (data: MeterFormData) => {
    try {
      set_is_submitting(true);

      const meter_payload: any = {
        meter_number: data.meter_number,
        client: parseInt(data.client_id),
        unit: null,
        installation_date: data.installation_date,
        status: data.status,
        initial_reading: data.initial_reading || 0,
        notes: [
          data.location,
          data.location_description,
          data.property_name,
          data.unit_number
        ].filter(Boolean).join(' | ') || undefined
      };

      console.log('Submitting meter payload:', meter_payload);

      if (meter_for_edit) {
        await update_meter(meter_for_edit.id, meter_payload);
        toast.success(`Meter ${data.meter_number} updated successfully`);
      } else {
        await create_meter(meter_payload);
        toast.success(`Meter ${data.meter_number} added successfully`);
      }

      set_is_meter_form_open(false);
      set_meter_for_edit(null);
      await load_meters();
    } catch (error: any) {
      console.error('Error saving meter:', error);
      
      if (error.response?.data) {
        const error_data = error.response.data;
        
        if (error_data.meter_number) {
          toast.error(`Meter number error: ${error_data.meter_number[0]}`);
        } else if (error_data.status) {
          toast.error(`Status error: ${error_data.status[0]}`);
        } else if (error_data.client) {
          toast.error(`Client error: ${error_data.client[0]}`);
        } else if (error_data.detail) {
          toast.error(error_data.detail);
        } else {
          const error_messages = Object.entries(error_data)
            .map(([field, messages]: [string, any]) => {
              const msg = Array.isArray(messages) ? messages[0] : messages;
              return `${field}: ${msg}`;
            })
            .join(', ');
          toast.error(`Validation errors: ${error_messages}`);
        }
      } else {
        toast.error('Failed to save meter');
      }
    } finally {
      set_is_submitting(false);
    }
  };

  const handle_delete_meter = async (meter: Meter) => {
    const confirm_message = `Are you sure you want to delete meter ${meter.meter_number}? This action cannot be undone.`;
    
    if (!window.confirm(confirm_message)) {
      return;
    }

    try {
      await delete_meter(meter.id);
      toast.success(`Meter ${meter.meter_number} deleted successfully`);
      await load_meters();
    } catch (error) {
      console.error('Error deleting meter:', error);
      toast.error('Failed to delete meter. It may have associated readings.');
    }
  };

  const handle_reading_submit = async (data: CreateMeterReadingPayload) => {
    try {
      set_is_submitting(true);

      let payload: FormData | CreateMeterReadingPayload = data;

      if (data.photo) {
        const form_data = new FormData();
        form_data.append('meter_id', data.meter_id);
        form_data.append('reading_value', data.reading_value.toString());
        form_data.append('reading_date', data.reading_date);
        
        if (data.notes) {
          form_data.append('notes', data.notes);
        }
        
        form_data.append('photo', data.photo);
        payload = form_data;
      }

      await create_meter_reading(payload);
      toast.success('Meter reading recorded successfully');
      
      set_is_reading_form_open(false);
      await load_meters();

      if (selected_meter) {
        set_current_view('history');
        await load_meter_history(selected_meter);
      }
    } catch (error) {
      console.error('Error saving reading:', error);
      toast.error('Failed to save meter reading');
    } finally {
      set_is_submitting(false);
    }
  };

  const handle_export = async () => {
    if (!meters || meters.length === 0) {
      toast.warning('No meters to export');
      return;
    }

    try {
      toast.info('Exporting meters...');

      const csv_data = meters.map(m => ({
        'Meter Number': m.meter_number || '',
        'Client': m.client_name || '',
        'Location': m.location_description || '',
        'Status': m.status || '',
        'Last Reading': m.last_reading_value?.toString() || 'N/A',
        'Last Reading Date': m.last_reading_date 
          ? new Date(m.last_reading_date).toLocaleDateString() 
          : 'N/A',
        'Installation Date': m.installation_date 
          ? new Date(m.installation_date).toLocaleDateString()
          : 'N/A'
      }));

      const headers = Object.keys(csv_data[0]);
      const csv_rows = csv_data.map(row => 
        headers.map(header => {
          const value = row[header as keyof typeof row];
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
      );
      
      const csv_content = [headers.join(','), ...csv_rows].join('\n');

      const blob = new Blob([csv_content], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `meters_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Meters exported successfully');
    } catch (error) {
      console.error('Error exporting meters:', error);
      toast.error('Failed to export meters');
    }
  };

  const handle_export_history = async () => {
    if (!selected_meter) {
      toast.warning('No meter selected');
      return;
    }

    if (!meter_readings || meter_readings.length === 0) {
      toast.warning('No reading history to export');
      return;
    }

    try {
      toast.info('Exporting reading history...');

      const csv_data = meter_readings.map(r => ({
        'Reading Date': r.reading_date 
          ? new Date(r.reading_date).toLocaleDateString() 
          : '',
        'Reading Value': r.reading_value?.toString() || '0',
        'Consumption': r.consumption?.toString() || '0',
        'Notes': r.notes || '',
        'Reader': r.reader_name || ''
      }));

      const headers = Object.keys(csv_data[0]);
      const csv_rows = csv_data.map(row => 
        headers.map(header => {
          const value = row[header as keyof typeof row];
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
      );
      
      const csv_content = [headers.join(','), ...csv_rows].join('\n');

      const blob = new Blob([csv_content], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const meter_number = selected_meter.meter_number || 'unknown';
      const date_str = new Date().toISOString().split('T')[0];
      link.download = `meter_${meter_number}_history_${date_str}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('History exported successfully');
    } catch (error) {
      console.error('Error exporting history:', error);
      toast.error('Failed to export history');
    }
  };

  if (is_loading) {
    return <PageLoading message="Loading meters..." />;
  }

  // Show history view
  if (current_view === 'history') {
    return (
      <>
        <MeterHistory
          meter={selected_meter}
          readings={meter_readings}
          stats={reading_stats}
          is_loading={history_loading}
          on_export={handle_export_history}
          on_back={handle_back_to_list}
        />
        
        {/* Keep reading form available even in history view */}
        <MeterReadingForm
          is_open={is_reading_form_open}
          on_close={() => set_is_reading_form_open(false)}
          on_submit={handle_reading_submit}
          meter={selected_meter}
          is_loading={is_submitting}
        />
      </>
    );
  }

  // Show list view
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-charcoal">Meters</h1>
          <p className="text-medium-gray mt-1">
            Monitor and manage water meters across your service area
          </p>
        </div>
      </div>

      {/* Meter List */}
      <MeterList
        meters={meters}
        is_loading={is_loading}
        on_add_meter={handle_add_meter}
        on_view_meter={handle_view_meter}
        on_edit_meter={handle_edit_meter}
        on_delete_meter={handle_delete_meter}
        on_add_reading={handle_add_reading}
        on_export={handle_export}
      />

      {/* Meter Reading Form */}
      <MeterReadingForm
        is_open={is_reading_form_open}
        on_close={() => set_is_reading_form_open(false)}
        on_submit={handle_reading_submit}
        meter={selected_meter}
        is_loading={is_submitting}
      />

      {/* Meter Form (Add/Edit) */}
      <MeterForm
        is_open={is_meter_form_open}
        on_close={() => set_is_meter_form_open(false)}
        on_submit={handle_meter_submit}
        meter={meter_for_edit}
        is_loading={is_submitting}
        clients={clients}
      />
    </div>
  );
};